import type { RecurringPayment } from '@/lib/api';

/**
 * Whether the series has a value in reais — the only currency the app sums.
 *
 * A BRL-priced series always does. A foreign one only once the bank reports the conversion; until
 * then `amount` is 0 by contract, so totals are already correct and this is what a surface reads
 * to SAY the figure is pending rather than zero. Same distinction `hasReportingAmount` draws for a
 * foreign-currency transaction on Lançamentos.
 */
export function hasConvertedAmount(item: RecurringPayment): boolean {
  if (item.price == null || item.price.currencyCode === 'BRL') return true;
  return item.price.converted;
}

/**
 * What the row displays as its charge: what actually settled when the server reported it,
 * otherwise the series' forecast. A settled row must show the same number everywhere it is
 * summed, or the card shows one charge at two values.
 */
export function recurringDisplayAmount(item: RecurringPayment): number {
  // Weekly is excluded: a settlement aggregates every charge in the period while `amount` is a
  // single week, so the two are different quantities and swapping them would overstate the row.
  if (item.cadence === 'Weekly') return item.amount;
  return item.settledThisPeriodAmount ?? item.amount;
}

/**
 * What the series contributes to THIS period's commitment.
 *
 * Reads `cycleForecastAmount` — the figure the server itself put into the group total — rather
 * than `amount`, which is a single week for a weekly series and a whole year's bill for a yearly
 * one billing in another month. Dividing a yearly bill by 12 here would make the period card
 * disagree with its own bar in "Compromissos por mês".
 */
export function recurringCycleAmount(item: RecurringPayment): number {
  if (!hasConvertedAmount(item)) return 0;
  const forecast = item.cycleForecastAmount ?? item.amount;
  if (item.cadence === 'Weekly') return forecast;
  return item.settledThisPeriodAmount ?? forecast;
}
